import React, { useState, useEffect } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity,
  ActivityIndicator, Dimensions
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';

const { width } = Dimensions.get('window');

const COLORS = {
  primary: '#1e293b',
  accent: '#2563eb',
  success: '#10b981',
  danger: '#ef4444',
  background: '#f1f5f9',
  card: '#ffffff',
  textMain: '#1e293b',
  textSub: '#64748b',
  border: '#e2e8f0'
};

export default function ReportsScreen() {
  const [loading, setLoading] = useState(true);
  const [mainData, setMainData] = useState(null); // Günlük hareketler, ciro vb. için
  const [kasaData, setKasaData] = useState(null); // Yeni API'den gelen kasalar için
  const [aylikGiderData, setAylikGiderData] = useState(0); // Yeni API'den gelen aylik gider

  const KASA_ID_ESLESTIRME = {
    1: 53,
    8: 55,
    7: 57
  };

  const parseMoneyValue = (value) => {
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    if (typeof value !== 'string') return 0;

    // "1.234,56 ₺" veya "1234.56" gibi farklı formatları sayıya çevirir.
    const normalized = value
      .replace(/\s/g, '')
      .replace('₺', '')
      .replace(/\./g, '')
      .replace(',', '.');

    const parsed = parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const getBugununCirosu = (data) => {
    if (!data) return 0;

    const directKeys = [
      'bugununCirosu',
      'bugunCiro',
      'bugunkuCiro',
      'gunlukCiro',
      'todayCiro',
      'todayRevenue'
    ];

    for (const key of directKeys) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        return parseMoneyValue(data[key]);
      }
    }

    if (!Array.isArray(data.gunlukCirolar) || data.gunlukCirolar.length === 0) {
      return 0;
    }

    const aylar = [
      'Ocak', 'Subat', 'Mart', 'Nisan', 'Mayis', 'Haziran',
      'Temmuz', 'Agustos', 'Eylul', 'Ekim', 'Kasim', 'Aralik'
    ];
    const bugun = new Date();
    const gun = bugun.getDate().toString();
    const ay = aylar[bugun.getMonth()];
    const yil = bugun.getFullYear().toString();

    const bugunKaydi = data.gunlukCirolar.find((item) => {
      const tarih = (item?.tarih || '').toString();
      return tarih.includes(gun) && tarih.includes(ay) && tarih.includes(yil);
    });

    if (bugunKaydi) {
      return parseMoneyValue(bugunKaydi.tutar);
    }

    // Bugün kaydı bulunamazsa listede en güncel kaydı varsayılan al.
    return parseMoneyValue(data.gunlukCirolar[0]?.tutar);
  };

  const birlestirilmisKasalar = (bugunkuKasalar, kasalar) => {
    const ciroToplamiByKasaId = (Array.isArray(bugunkuKasalar) ? bugunkuKasalar : []).reduce((acc, item) => {
      const hedefKasaId = KASA_ID_ESLESTIRME[item?.kasaID];
      if (!hedefKasaId) return acc;

      const ciro = parseMoneyValue(item?.ciro);
      acc[hedefKasaId] = (acc[hedefKasaId] || 0) + ciro;
      return acc;
    }, {});

    return (Array.isArray(kasalar) ? kasalar : []).map((kasa) => {
      const mevcutBakiye = parseMoneyValue(kasa?.bakiye);
      const eklenecekCiro = ciroToplamiByKasaId[kasa?.kasa_id] || 0;

      return {
        ...kasa,
        bakiye: mevcutBakiye + eklenecekCiro
      };
    });
  };

  useEffect(() => {
    fetchReportData();
  }, []);

  const fetchReportData = async () => {
    try {
      setLoading(true);

      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

      const formatDate = (date) => {
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
      };

      const giderBody = JSON.stringify({
        baslangic: `${formatDate(startOfMonth)} 00:00:00`,
        bitis: `${formatDate(endOfMonth)} 23:59:59`
      });

      // API'lere aynı anda istek atarak bekleme süresini optimize ediyoruz
      const [mainResponse, kasaResponse, giderResponse] = await Promise.all([
        fetch('https://mirwax.durumrapor.com/app.php?action=mirwax_anasayfa'),
        fetch('https://durumrapor.com/API/yikama-kasa-raporu'),
        fetch('https://www.durumrapor.com/API/yikama-odemeleri-cek', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: giderBody
        })
      ]);

      const mainJson = await mainResponse.json();
      const kasaJson = await kasaResponse.json();
      const giderJson = await giderResponse.json();
      console.log(giderJson)

      setMainData(mainJson);

      let totalGider = 0;
      if (giderJson.status === 'true' && Array.isArray(giderJson.data)) {
        totalGider = giderJson.data.reduce((acc, curr) => acc + parseFloat(curr.Tutar || 0), 0);
      }
      setAylikGiderData(totalGider);

      // Yeni API'nin başarı durumunu kontrol edip kasaları state'e atıyoruz
      if (kasaJson.status === 'success') {
        const birlesikKasalar = birlestirilmisKasalar(
          mainJson?.bugunkuKasalar,
          kasaJson?.data?.kasalar
        );
        setKasaData(birlesikKasalar);
      } else {
        setKasaData([]);
      }

    } catch (error) {
      console.error("Rapor Hatası:", error);
    } finally {
      setLoading(false);
    }
  };

  // Veriler yüklenene kadar veya boşsa loading göster
  if (loading || !mainData || !kasaData) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={COLORS.accent} />
      </View>
    );
  }

  // Yeni API'deki kasaların toplamını hesaplıyoruz
  const aylikGelir = parseMoneyValue(mainData?.aylikCiro);
  const aylikGider = aylikGiderData;
  const aylikNetKazanc = aylikGelir - aylikGider;
  const bugununCirosu = getBugununCirosu(mainData);

  // Eski arayüz yapısını bozmamak için yeni kasa datasını eski formata uyarlıyoruz
  const kasaListesi = kasaData.map(kasa => ({
    id: kasa.kasa_id.toString(),
    Adi: kasa.kasa_adi,
    NetBakiye: kasa.bakiye
  }));

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>Finansal Özet</Text>
        <TouchableOpacity onPress={fetchReportData} style={styles.refreshBtn}>
          <Ionicons name="sync-outline" size={18} color={COLORS.accent} />
        </TouchableOpacity>
      </View>

      <ScrollView showsVerticalScrollIndicator={false}>

        {/* 1. ÜST PANEL: AYLIK FİNANSAL ÖZET */}
        <View style={styles.topPanelContainer}>
          <View style={styles.monthlySummaryCard}>
            <Text style={styles.monthlySummaryTitle}>Aylık Finansal Özet</Text>

            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>Aylık Gelir (Ciro)</Text>
              <Text style={[styles.summaryValue, { color: COLORS.textMain }]}>
                {aylikGelir.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
              </Text>
            </View>

            <View style={styles.summaryDivider} />

            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>Aylık Gider</Text>
              <Text style={[styles.summaryValue, { color: COLORS.danger }]}>
                {aylikGider.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
              </Text>
            </View>

            <View style={styles.summaryDivider} />

            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabelStrong}>Aylık Net Kazanç</Text>
              <Text style={[styles.summaryValueStrong, { color: aylikNetKazanc >= 0 ? COLORS.success : COLORS.danger }]}>
                {aylikNetKazanc.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
              </Text>
            </View>
          </View>

        </View>

        {/* 2. KASA BAKİYELERİ */}
        <Text style={styles.sectionTitle}>Bireysel Kasalar</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.kasaScroll}>
          {kasaListesi.map((kasa) => (
            <View key={kasa.id} style={styles.miniKasaCard}>
              <View style={styles.miniKasaTop}>
                {/* İkonu 'Nakit' kelimesine göre güncelledik */}
                <Ionicons
                  name={kasa.Adi.toLowerCase().includes('nakit') ? 'cash' : 'card'}
                  size={14}
                  color={COLORS.accent}
                />
                <Text style={styles.miniKasaLabel}>{kasa.Adi}</Text>
              </View>
              <Text style={styles.miniKasaValue}>
                {kasa.NetBakiye.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
              </Text>
            </View>
          ))}
        </ScrollView>

        {/* 3. GÜNLÜK HAREKETLER (Eski mainData'dan gelmeye devam ediyor) */}
        <Text style={styles.sectionTitle}>Günlük Hareketler</Text>
        <View style={styles.listCard}>
          {mainData.gunlukCirolar.map((gun, index) => (
            <View key={index} style={[styles.listRow, index === mainData.gunlukCirolar.length - 1 && { borderBottomWidth: 0 }]}>
              <View style={styles.dateCircle}>
                <Text style={styles.dateNum}>{gun.tarih.split(' ')[0]}</Text>
              </View>
              <View style={styles.dateInfo}>
                <Text style={styles.dateMonth}>{gun.tarih.split(' ')[1]} {gun.tarih.split(' ')[2].replace(',', '')}</Text>
                <Text style={styles.dateDay}>{gun.tarih.split(',')[1].trim()}</Text>
              </View>
              <View style={styles.amountInfo}>
                <Text style={styles.amountText}>{gun.tutar}</Text>
                <View style={styles.statusDotRow}>
                  <View style={[styles.dot, { backgroundColor: COLORS.success }]} />
                  <Text style={styles.statusText}>Tahsil Edildi</Text>
                </View>
              </View>
            </View>
          ))}
        </View>

      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: COLORS.background },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  header: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    padding: 20, backgroundColor: COLORS.card, borderBottomWidth: 1, borderBottomColor: COLORS.border
  },
  headerTitle: { fontSize: 18, fontWeight: '800', color: COLORS.textMain },
  refreshBtn: { width: 36, height: 36, borderRadius: 10, backgroundColor: COLORS.background, justifyContent: 'center', alignItems: 'center' },

  sectionTitle: { fontSize: 12, fontWeight: '800', color: COLORS.textSub, marginHorizontal: 20, marginTop: 25, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 },

  topPanelContainer: { padding: 15 },
  monthlySummaryCard: {
    backgroundColor: COLORS.card,
    borderRadius: 20,
    padding: 16,
    borderWidth: 1,
    borderColor: COLORS.border
  },
  monthlySummaryTitle: {
    fontSize: 13,
    fontWeight: '800',
    color: COLORS.textMain,
    marginBottom: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.8
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  summaryLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: COLORS.textSub
  },
  summaryLabelStrong: {
    fontSize: 12,
    fontWeight: '800',
    color: COLORS.textMain
  },
  summaryValue: {
    fontSize: 15,
    fontWeight: '800'
  },
  summaryValueStrong: {
    fontSize: 17,
    fontWeight: '900'
  },
  summaryDivider: {
    height: 1,
    backgroundColor: COLORS.border,
    marginVertical: 10
  },

  todayCard: {
    marginTop: 10,
    backgroundColor: COLORS.card,
    borderRadius: 20,
    padding: 14,
    borderWidth: 1,
    borderColor: COLORS.border
  },
  todayCardTop: {
    flexDirection: 'row',
    alignItems: 'center'
  },
  todayCardLabel: {
    marginLeft: 6,
    fontSize: 10,
    fontWeight: '700',
    color: COLORS.textSub,
    letterSpacing: 0.5
  },
  todayCardValue: {
    marginTop: 8,
    fontSize: 22,
    fontWeight: '800',
    color: COLORS.textMain
  },

  kasaScroll: { paddingLeft: 15 },
  miniKasaCard: {
    backgroundColor: COLORS.card, width: 140, padding: 12, borderRadius: 15, marginRight: 10,
    borderWidth: 1, borderColor: COLORS.border
  },
  miniKasaTop: { flexDirection: 'row', alignItems: 'center', marginBottom: 4 },
  miniKasaLabel: { fontSize: 10, fontWeight: '700', color: COLORS.textSub, marginLeft: 5 },
  miniKasaValue: { fontSize: 14, fontWeight: '800', color: COLORS.textMain },

  listCard: { backgroundColor: COLORS.card, marginHorizontal: 15, borderRadius: 24, paddingHorizontal: 15, marginBottom: 30, borderWidth: 1, borderColor: COLORS.border },
  listRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 15, borderBottomWidth: 1, borderBottomColor: COLORS.background },
  dateCircle: { width: 40, height: 40, borderRadius: 12, backgroundColor: COLORS.background, justifyContent: 'center', alignItems: 'center', marginRight: 15 },
  dateNum: { fontSize: 16, fontWeight: '800', color: COLORS.accent },
  dateInfo: { flex: 1 },
  dateMonth: { fontSize: 14, fontWeight: '700', color: COLORS.textMain },
  dateDay: { fontSize: 11, color: COLORS.textSub, marginTop: 1 },
  amountInfo: { alignItems: 'flex-end' },
  amountText: { fontSize: 15, fontWeight: '800', color: COLORS.textMain },
  statusDotRow: { flexDirection: 'row', alignItems: 'center', marginTop: 2 },
  dot: { width: 6, height: 6, borderRadius: 3, marginRight: 4 },
  statusText: { fontSize: 9, fontWeight: '700', color: COLORS.success, textTransform: 'uppercase' }
});